import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Calendar as A1, Clock as A2, BookOpen as A3, Bell as A4, CheckCircle as A5, Target as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import { ScoreVsMySchools } from '../components/ScoreVsMySchools';
import { Button } from '../components/ui/button';
import { 
  Calendar, BookOpen, ExternalLink, Plus, Clock, Target,
  TrendingUp, Award, AlertCircle, CheckCircle, Star, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static exam metadata (sections / scores / resources / tips).
// DATES come from the backend (`/api/exams/dates`), see useEffect below.
const EXAM_META = [
  {
    id: 'sat',
    name: 'SAT',
    fullName: 'Scholastic Assessment Test',
    category: 'college_admission',
    icon: Target,
    color: 'blue',
    description: 'Primary college admission test accepted by most US universities. Tests reading, writing, and math.',
    duration: '3 hours',
    cost: '$60 (fee waivers available)',
    sections: [
      { name: 'Reading & Writing', questions: '54 questions', time: '64 min', maxScore: 800 },
      { name: 'Math', questions: '44 questions', time: '80 min', maxScore: 800 },
    ],
    scores: {
      maxScore: 1600,
      avgScore: 1050,
      topScore: '1400+ (top 10%)',
      perfectScore: 1600,
    },
    resources: [
      { name: 'Official SAT Practice (College Board)', url: 'https://satsuite.collegeboard.org/sat/practice-preparation', type: 'Free', featured: true },
      { name: 'Khan Academy SAT Prep', url: 'https://www.khanacademy.org/sat', type: 'Free', featured: true },
      { name: 'The Official SAT Study Guide', url: 'https://www.amazon.com/Official-SAT-Study-Guide-2024/dp/1457316706', type: 'Book ($25)' },
      { name: 'Princeton Review SAT Prep', url: 'https://www.princetonreview.com/college/sat-test-prep', type: 'Course ($$$)' },
      { name: 'Kaplan SAT Prep', url: 'https://www.kaptest.com/sat', type: 'Course ($$$)' },
    ],
    tips: [
      'Take official practice tests under timed conditions',
      'Focus on your weakest sections first',
      'Learn to pace yourself - time management is key',
      'Review wrong answers to understand mistakes',
    ],
  },
  {
    id: 'act',
    name: 'ACT',
    fullName: 'American College Testing',
    category: 'college_admission',
    icon: Target,
    color: 'red',
    description: 'Alternative to SAT, tests English, Math, Reading, Science reasoning. Optional writing section.',
    duration: '2 hours 55 min (+ 40 min writing)',
    cost: '$68 without writing, $93 with writing',
    sections: [
      { name: 'English', questions: '75 questions', time: '45 min', maxScore: 36 },
      { name: 'Math', questions: '60 questions', time: '60 min', maxScore: 36 },
      { name: 'Reading', questions: '40 questions', time: '35 min', maxScore: 36 },
      { name: 'Science', questions: '40 questions', time: '35 min', maxScore: 36 },
    ],
    scores: {
      maxScore: 36,
      avgScore: 20,
      topScore: '30+ (top 10%)',
      perfectScore: 36,
    },
    resources: [
      { name: 'Official ACT Practice', url: 'https://www.act.org/content/act/en/products-and-services/the-act/test-preparation.html', type: 'Free', featured: true },
      { name: 'ACT Academy', url: 'https://academy.act.org', type: 'Free', featured: true },
      { name: 'The Official ACT Prep Guide', url: 'https://www.amazon.com/Official-ACT-Prep-Guide-2024/dp/1394196520', type: 'Book ($30)' },
      { name: 'Magoosh ACT Prep', url: 'https://magoosh.com/act/', type: 'Course ($$)' },
    ],
    tips: [
      'Science section tests reading graphs, not science knowledge',
      'No penalty for wrong answers - answer every question',
      'ACT math includes more advanced topics than SAT',
      'Practice reading passages quickly for time management',
    ],
  },
  {
    id: 'psat',
    name: 'PSAT/NMSQT',
    fullName: 'Preliminary SAT / National Merit Scholarship Qualifying Test',
    category: 'college_admission',
    icon: Star,
    color: 'purple',
    description: 'Practice for SAT and qualifier for National Merit Scholarship. Taken junior year.',
    duration: '2 hours 45 min',
    cost: '$18 (often covered by schools)',
    sections: [
      { name: 'Reading & Writing', questions: '54 questions', time: '64 min', maxScore: 760 },
      { name: 'Math', questions: '44 questions', time: '70 min', maxScore: 760 },
    ],
    scores: {
      maxScore: 1520,
      avgScore: 920,
      topScore: '1400+ (National Merit)',
      perfectScore: 1520,
    },
    resources: [
      { name: 'PSAT Practice on Khan Academy', url: 'https://www.khanacademy.org/sat', type: 'Free', featured: true },
      { name: 'College Board PSAT Practice', url: 'https://satsuite.collegeboard.org/psat-nmsqt/practice-preparation', type: 'Free', featured: true },
    ],
    tips: [
      'Junior year PSAT counts for National Merit',
      'Great low-stakes practice for SAT',
      'Top scorers can earn significant scholarships',
    ],
  },
  {
    id: 'ap',
    name: 'AP Exams',
    fullName: 'Advanced Placement Exams',
    category: 'college_credit',
    icon: Award,
    color: 'green',
    description: 'Earn college credit in high school. Scores of 3-5 can earn credit at most universities.',
    duration: '2-3 hours per exam',
    cost: '$98 per exam (fee reductions available)',
    sections: [
      { name: 'Multiple Choice', questions: 'Varies', time: '~90 min', maxScore: 'Varies' },
      { name: 'Free Response', questions: 'Varies', time: '~90 min', maxScore: 'Varies' },
    ],
    scores: {
      maxScore: 5,
      avgScore: '3.0 across all exams',
      topScore: '5 (Extremely qualified)',
      perfectScore: 5,
    },
    resources: [
      { name: 'AP Classroom', url: 'https://apcentral.collegeboard.org/courses', type: 'Free', featured: true },
      { name: 'Fiveable', url: 'https://fiveable.me', type: 'Free', featured: true },
      { name: 'Princeton Review AP Books', url: 'https://www.princetonreview.com/college/ap-test-prep', type: 'Books ($15-20)' },
      { name: 'Barrons AP Prep', url: 'https://www.barronspublishing.com', type: 'Books ($15-20)' },
    ],
    tips: [
      'Score of 3+ typically earns college credit',
      'Taking AP classes boosts GPA and shows rigor',
      'Most popular: AP US History, AP English, AP Calculus',
      'Some colleges want 4+ for credit',
    ],
  },
  {
    id: 'clep',
    name: 'CLEP',
    fullName: 'College-Level Examination Program',
    category: 'college_credit',
    icon: BookOpen,
    color: 'cyan',
    description: 'Earn college credit by demonstrating knowledge. 34 exams in 5 subject areas.',
    duration: '90-120 minutes',
    cost: '$93 per exam',
    sections: [
      { name: 'Multiple Choice', questions: '~100 questions', time: '90 min', maxScore: 80 },
    ],
    scores: {
      maxScore: 80,
      avgScore: 50,
      topScore: '50+ (passing at most schools)',
      perfectScore: 80,
    },
    resources: [
      { name: 'Official CLEP Study Resources', url: 'https://clep.collegeboard.org/clep-exams', type: 'Free', featured: true },
      { name: 'Modern States', url: 'https://modernstates.org', type: 'Free courses + free exam vouchers', featured: true },
      { name: 'Peterson\'s CLEP Prep', url: 'https://www.petersons.com/testprep/clep-test-prep.aspx', type: 'Course ($$)' },
    ],
    tips: [
      'Modern States offers FREE courses and exam vouchers',
      'Can save thousands in college tuition',
      'Check if your target college accepts CLEP',
      'Great for self-motivated learners',
    ],
  },
  {
    id: 'toefl',
    name: 'TOEFL',
    fullName: 'Test of English as a Foreign Language',
    category: 'english_proficiency',
    icon: BookOpen,
    color: 'amber',
    description: 'Required for international students. Tests academic English proficiency.',
    duration: '2 hours',
    cost: '$185-$245 (varies by country)',
    sections: [
      { name: 'Reading', questions: '20 questions', time: '35 min', maxScore: 30 },
      { name: 'Listening', questions: '28 questions', time: '36 min', maxScore: 30 },
      { name: 'Speaking', questions: '4 tasks', time: '16 min', maxScore: 30 },
      { name: 'Writing', questions: '2 tasks', time: '29 min', maxScore: 30 },
    ],
    scores: {
      maxScore: 120,
      avgScore: 83,
      topScore: '100+ (competitive for top schools)',
      perfectScore: 120,
    },
    resources: [
      { name: 'Official TOEFL Practice', url: 'https://www.ets.org/toefl/test-takers/ibt/prepare', type: 'Free', featured: true },
      { name: 'TOEFL Official Guide', url: 'https://www.amazon.com/Official-Guide-TOEFL-Test-Sixth/dp/1260470350', type: 'Book ($25)' },
      { name: 'Magoosh TOEFL', url: 'https://magoosh.com/toefl/', type: 'Course ($$)' },
    ],
    tips: [
      'Most top universities require 100+',
      'Practice speaking English out loud daily',
      'Note-taking skills are essential',
    ],
  },
];

export default function ExamDatesPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [selectedExam, setSelectedExam] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [datesByExam, setDatesByExam] = useState({});
  const [datesLoading, setDatesLoading] = useState(true);

  // Map exam id (frontend) to backend exam_type key.
  // Backend uses uppercase keys: SAT, ACT, PSAT, AP_Exams, GED, TEAS, ASVAB.
  const ID_TO_BACKEND = {
    sat: 'SAT', act: 'ACT', psat: 'PSAT', ap: 'AP_Exams',
    clep: 'CLEP', toefl: 'TOEFL',
  };

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API}/exams/dates`, {
          ...getAuthHeader(),
          withCredentials: true,
        });
        setDatesByExam(res.data.exams || {});
      } catch (err) {
        // Public fallback for unauthenticated visitors
        try {
          const pub = await axios.get(`${API}/exams/dates/public`);
          setDatesByExam(pub.data.exams || {});
        } catch (_) {
          setDatesByExam({});
        }
      } finally {
        setDatesLoading(false);
      }
    };
    fetchDates();
  }, [getAuthHeader]);

  // Format ISO date "2026-03-14" → "March 14, 2026". Pass-through for non-ISO strings.
  const formatDate = (raw) => {
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const d = new Date(raw + 'T00:00:00Z');
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    }
    return raw;
  };

  // Merge static metadata + live dates from backend
  const EXAMS = EXAM_META.map((meta) => {
    const backendKey = ID_TO_BACKEND[meta.id];
    const apiDates = (datesByExam[backendKey] || []).map((d) => ({
      date: formatDate(d.date),
      deadline: formatDate(d.registration_deadline) || d.note || 'See registration site',
    }));
    return { ...meta, dates: apiDates };
  });

  const addToGoals = async (item, category = 'certification') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item,
        description: `Exam preparation goal`,
        category: category,
        priority: 'high',
        status: 'backlog',
        source: 'Exam Dates'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  const filteredExams = filterCategory === 'all' 
    ? EXAMS 
    : EXAMS.filter(e => e.category === filterCategory);

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="exams-arrival"
        eyebrow="Exam Dates & Prep"
        title="Never miss a date that matters."
        subtitle="Registration windows, test dates, score release and the prep runway before each one, laid out so you are ready rather than surprised."
        accent="#60a5fa"
        second="#fbbf24"
        ctaLabel="Show my dates"
        narration={"This is Exam Dates and Prep. Missed registration windows cost people whole semesters, so every date that matters for your path is here: when registration opens and closes, the test dates, when scores land, and how long you realistically need to prepare. Add one and it becomes a dated step in your plan with reminders attached."}
        facts={[
          { icon: A1, head: "Every date", sub: "Registration, test day and score release" },
          { icon: A2, head: "Prep runway", sub: "How long you need before each one" },
          { icon: A4, head: "Reminders", sub: "Warned in time to actually register" }
        ]}
        modules={[
          { icon: A1, label: "Test calendar", line: "All the dates for the tests you need", hue: "#60a5fa" },
          { icon: A2, label: "Registration windows", line: "Open and close dates, with fees", hue: "#fbbf24" },
          { icon: A3, label: "Prep plan", line: "What to study, in what order, from where", hue: "#a78bfa" },
          { icon: A6, label: "Target scores", line: "What your options actually require", hue: "#22d3ee" },
          { icon: A5, label: "Results", line: "Scores recorded against your plan", hue: "#34d399" },
          { icon: A4, label: "Alerts", line: "A nudge before every deadline", hue: "#f472b6" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Exam Dates & Test Prep"
        description="Your score against the schools you saved, the dates you still have, and what to do between now and then."
      />

      <ScoreVsMySchools />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <GlassCard className="text-center">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-blue-400" />
          <p className="text-2xl font-bold text-white">{EXAMS.length}</p>
          <p className="text-sm text-slate-300">Major Exams</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Target className="w-8 h-8 mx-auto mb-2 text-green-400" />
          <p className="text-2xl font-bold text-white">1600</p>
          <p className="text-sm text-slate-300">Perfect SAT Score</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Award className="w-8 h-8 mx-auto mb-2 text-purple-400" />
          <p className="text-2xl font-bold text-white">38</p>
          <p className="text-sm text-slate-300">AP Subjects</p>
        </GlassCard>
        <GlassCard className="text-center">
          <DollarSign className="w-8 h-8 mx-auto mb-2 text-amber-400" />
          <p className="text-2xl font-bold text-white">FREE</p>
          <p className="text-sm text-slate-300">Practice Resources</p>
        </GlassCard>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'All Exams' },
          { id: 'college_admission', label: 'College Admission' },
          { id: 'college_credit', label: 'College Credit' },
          { id: 'english_proficiency', label: 'English Proficiency' },
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => setFilterCategory(filter.id)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              filterCategory === filter.id
                ? 'bg-blue-600 text-white'
                : 'bg-navy-800 text-slate-300 hover:text-white'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Exams Grid */}
      <div className="space-y-6">
        {filteredExams.map(exam => {
          const Icon = exam.icon;
          const isExpanded = selectedExam?.id === exam.id;
          
          return (
            <GlassCard 
              key={exam.id}
              className={`cursor-pointer transition-all ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setSelectedExam(isExpanded ? null : exam)}
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                {/* Exam Header */}
                <div className={`w-16 h-16 bg-${exam.color}-500/20 rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-8 h-8 text-${exam.color}-400`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{exam.name}</h3>
                      <p className="text-sm text-slate-300">{exam.fullName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-400">{exam.scores.maxScore}</p>
                      <p className="text-xs text-slate-300">Max Score</p>
                    </div>
                  </div>
                  
                  <p className="text-slate-300 mt-2">{exam.description}</p>
                  
                  {/* Quick Stats Row */}
                  <div className="flex flex-wrap gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-1 text-slate-300">
                      <Clock className="w-4 h-4" />
                      {exam.duration}
                    </div>
                    <div className="flex items-center gap-1 text-slate-300">
                      <DollarSign className="w-4 h-4" />
                      {exam.cost}
                    </div>
                    <div className="flex items-center gap-1 text-blue-400">
                      <TrendingUp className="w-4 h-4" />
                      Avg: {exam.scores.avgScore}
                    </div>
                    <div className="flex items-center gap-1 text-green-400">
                      <Star className="w-4 h-4" />
                      Top: {exam.scores.topScore}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-6 pt-6 border-t border-navy-700 space-y-6">
                  {/* Test Sections */}
                  <div>
                    <h4 className="font-semibold text-white mb-3">Test Sections</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {exam.sections.map((section, i) => (
                        <div key={i} className="p-3 bg-navy-900/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{section.name}</span>
                            <span className="text-emerald-400 font-bold">{section.maxScore}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-300">
                            <span>{section.questions}</span>
                            <span>•</span>
                            <span>{section.time}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Upcoming Dates */}
                  <div>
                    <h4 className="font-semibold text-white mb-3">Upcoming Test Dates</h4>
                    {datesLoading ? (
                      <p className="text-sm text-slate-300" data-testid="exam-dates-loading">Loading dates…</p>
                    ) : exam.dates.length === 0 ? (
                      <p className="text-sm text-slate-300" data-testid={`no-dates-${exam.id}`}>
                        No upcoming dates published yet. Check back soon or visit the official site.
                      </p>
                    ) : (
                      <div className="grid md:grid-cols-3 gap-3">
                        {exam.dates.slice(0, 6).map((dateInfo, i) => (
                          <div key={i} className="p-3 bg-navy-900/50 rounded-lg" data-testid={`exam-date-${exam.id}-${i}`}>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-400" />
                              <span className="text-white">{dateInfo.date}</span>
                            </div>
                            <p className="text-xs text-slate-300 mt-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              Register by: {dateInfo.deadline}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Study Resources */}
                  <div>
                    <h4 className="font-semibold text-white mb-3">Study Resources</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {exam.resources.map((resource, i) => (
                        <a
                          key={i}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                            resource.featured 
                              ? 'bg-green-900/30 border border-green-500/30 hover:bg-green-900/50' 
                              : 'bg-navy-900/50 hover:bg-navy-800'
                          }`}
                        >
                          <div>
                            <span className="text-white">{resource.name}</span>
                            <span className={`text-sm block ${resource.featured ? 'text-green-400' : 'text-slate-300'}`}>
                              {resource.type}
                              {resource.featured && ' ⭐ Recommended'}
                            </span>
                          </div>
                          <ExternalLink className="w-4 h-4 text-blue-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                  
                  {/* Tips */}
                  <div>
                    <h4 className="font-semibold text-white mb-3">Pro Tips</h4>
                    <ul className="space-y-2">
                      {exam.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToGoals(`Prepare for ${exam.name}`);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add "{exam.name} Prep" to Goals
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToGoals(`Register for ${exam.name} - ${exam.dates[0]?.date || 'upcoming'}`);
                      }}
                      variant="outline"
                      className="border-navy-600"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Add Registration Reminder
                    </Button>
                  </div>
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>
    </AppLayout>
  );
}
